import { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import styled from "styled-components";
import {
  Button,
  Heading,
  TextInput,
  RadioButton,
  RadioButtonGroup,
  Checkbox,
  CheckboxGroup,
  InlineAlert,
} from "suomifi-ui-components";
import { IconArrowLeft } from "suomifi-icons";
import {
  voiNahda,
  PERUSTYO_VAIHTOEHDOT,
  type Perustyo,
  profiiliToGroupIds,
} from "../utils/oikeudet";
import { useTodellinenRooli } from "../hooks/useTodellinenRooli";
import { useCreateOfficerMutation } from "../api/backendApi";
import { useTranslation } from "react-i18next";

/**
 * Virkailijan lisäys yhdellä lomakkeella (työpaja 22.6.2026, E4).
 *
 * Aiemmin pilkottu: "+ Lisää virkailija" avasi modaalin (vain sähköposti) →
 * erillinen myöntökortti rooleille. E4: yhdistä yhdelle sivulle — nimi +
 * toimikortin tunniste ylös, sähköposti lisätietona, rooli + lisäoikeudet
 * samalle sivulle. Tallennus luo virkailijan rooleineen kerralla.
 *
 * E2/E3 (mock): tunnistautuminen virkailijakortilla — ei Suomi.fi-tunnistusta
 * eikä HETU:a. Toimikortin tunniste on placeholder, koska täsmällinen tunniste
 * on Digian + DVV:n selvitettävä.
 */

const Page = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.m};
`;

/* Kortit ovat täysleveitä, mutta lomakekentät rajataan miellyttävän levyisiksi
   (ei venytetä koko kortin leveyteen leveällä näytöllä). */
const Kentat = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.m};
  max-width: 32rem;
`;

const BackButton = styled.button`
  background: transparent;
  border: 0;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs};
  font: ${({ theme }) => theme.typography.bodySemiBoldSmall};
  font-size: 0.875rem;
  color: ${({ theme }) => theme.colors.highlightBase};
  padding: 0;
  align-self: flex-start;

  &:hover {
    text-decoration: underline;
  }
  & > svg {
    width: 1rem;
    height: 1rem;
  }
`;

const Kortti = styled.div`
  background: ${({ theme }) => theme.colors.whiteBase};
  border: 1px solid ${({ theme }) => theme.colors.depthLight1};
  padding: ${({ theme }) => theme.spacing.m};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.m};
`;

const SektioOtsikko = styled.div`
  font: ${({ theme }) => theme.typography.bodySemiBoldSmall};
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: ${({ theme }) => theme.colors.brandBase};
  border-bottom: 2px solid ${({ theme }) => theme.colors.brandBase};
  padding-bottom: ${({ theme }) => theme.spacing.xxs};
`;

const AdminHuomio = styled.p`
  font: ${({ theme }) => theme.typography.bodyTextSmall};
  font-size: 0.8125rem;
  color: ${({ theme }) => theme.colors.depthDark1};
  font-style: italic;
  margin: 0;
`;

const Toiminnot = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.s};
  flex-wrap: wrap;
`;

export default function VirkailijanLisays() {
  const { t } = useTranslation("virkailijanLisays");
  const navigate = useNavigate();

  const [etunimi, setEtunimi] = useState("");
  const [sukunimi, setSukunimi] = useState("");
  const [toimikortti, setToimikortti] = useState("");
  const [sahkoposti, setSahkoposti] = useState("");
  const [perustyo, setPerustyo] = useState<Perustyo>("ei");
  const [lisaRaportit, setLisaRaportit] = useState(false);
  const [lisaMyonto, setLisaMyonto] = useState(false);
  const [virhe, setVirhe] = useState(false);
  const [createOfficer] = useCreateOfficerMutation();
  const piilotaRaportit = import.meta.env.VITE_PIILOTA_RAPORTIT === "true";
  // Lisäyksen epäonnistuminen (esim. tuleva backend-virhe). Mockissa lisäys
  // ei voi epäonnistua, joten tämä on toistaiseksi lepäävä virhepolku.
  const [lisaysEpaonnistui, setLisaysEpaonnistui] = useState(false);
  const { todellinenRooli, isLoading } = useTodellinenRooli();
  const KAYTA_SALASANAA =
    import.meta.env.VITE_USE_PASSWORD_WHEN_CREATING_USER === "true";

  const [salasana, setSalasana] = useState("");

  if (isLoading) return <>{t("common:ladataan")}</>;

  if (!voiNahda("navi.kayttooikeudet", todellinenRooli?.profiili)) {
    return <Navigate to="/" replace />;
  }

  const adminValittu = perustyo === "admin";

  const etunimiVirhe = !etunimi.trim();
  const sukunimiVirhe = !sukunimi.trim();

  const emailValidationRegex =
    /^[a-zA-Z0-9._+%-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

  function isValidEmail(email: string): boolean {
    const trimmed = email.trim();
    return emailValidationRegex.test(trimmed) && trimmed.length <= 254;
  }

  const sahkopostiVirhe = !isValidEmail(sahkoposti);

  async function tallenna() {
    setLisaysEpaonnistui(false);
    // Validointi: nimi + sähköposti pakollisia. Toimikortin tunniste on
    // placeholder (E3 avoin) → ei pakollinen.
    if (etunimiVirhe || sukunimiVirhe || sahkopostiVirhe) {
      setVirhe(true);
      // WCAG-hyvä käytäntö: siirrä fokus ensimmäiseen virheelliseen kenttään.
      // rAF varmistaa että error-tila (aria-invalid + statusText) on ehtinyt
      // renderöityä ennen fokusta, jotta ruudunlukija ilmoittaa virheen.
      const ekaVirhe = etunimiVirhe
        ? "lisays-etunimi"
        : sukunimiVirhe
        ? "lisays-sukunimi"
        : "lisays-sahkoposti";
      requestAnimationFrame(() => {
        document.getElementById(ekaVirhe)?.focus();
      });
      return;
    }

    const profiili = {
      perustyo,
      lisaoikeudet: {
        raportit: lisaRaportit || adminValittu,
        myonto: lisaMyonto || adminValittu,
      },
    };
    try {
      const uusi = await createOfficer({
        email: sahkoposti.trim(),
        name: { givenName: etunimi.trim(), familyName: sukunimi.trim() },
        memberOf: profiiliToGroupIds(profiili),
        ...(KAYTA_SALASANAA && salasana ? { password: salasana } : {}),
      }).unwrap();

      navigate("/kayttooikeudet", {
        state: {
          lisattyNimi: etunimi.trim() + sukunimi.trim(),
          lisattyUid: uusi.id,
        },
      });
    } catch {
      setLisaysEpaonnistui(true);
    }
  }

  return (
    <Page>
      <BackButton type="button" onClick={() => navigate("/kayttooikeudet")}>
        <IconArrowLeft aria-hidden />
        {t("napit.takaisin")}
      </BackButton>

      <Heading variant="h1">{t("otsikot.lisaa_virkailija")}</Heading>

      <Kortti>
        <SektioOtsikko>{t("otsikot.tiedot")}</SektioOtsikko>
        <Kentat>
          <TextInput
            id="lisays-etunimi"
            labelText={t("labelText.etunimi")}
            value={etunimi}
            onChange={(v) => setEtunimi(String(v ?? ""))}
            status={virhe && etunimiVirhe ? "error" : "default"}
            statusText={virhe && etunimiVirhe ? t("virhe.etunimi") : undefined}
            fullWidth
          />
          <TextInput
            id="lisays-sukunimi"
            labelText={t("labelText.sukunimi")}
            value={sukunimi}
            onChange={(v) => setSukunimi(String(v ?? ""))}
            status={virhe && sukunimiVirhe ? "error" : "default"}
            statusText={
              virhe && sukunimiVirhe ? t("virhe.sukunimi") : undefined
            }
            fullWidth
          />
          <TextInput
            id="lisays-sahkoposti"
            labelText={t("labelText.sahkoposti")}
            value={sahkoposti}
            onChange={(v) => setSahkoposti(String(v ?? ""))}
            status={virhe && sahkopostiVirhe ? "error" : "default"}
            statusText={
              virhe && sahkopostiVirhe ? t("virhe.sahkoposti") : undefined
            }
            fullWidth
          />
          {KAYTA_SALASANAA && (
            <TextInput
              id="lisays-salasana"
              labelText={t("labelText.salasana")}
              type="password"
              value={salasana}
              onChange={(v) => setSalasana(String(v ?? ""))}
              status={"default"}
              statusText={""}
            />
          )}
        </Kentat>
      </Kortti>

      <Kortti>
        <SektioOtsikko>{t("otsikot.perustyo")}</SektioOtsikko>
        <RadioButtonGroup
          labelText={t("labelText.perustyo")}
          labelMode="hidden"
          name="perustyo"
          value={perustyo}
          onChange={(v: string) => setPerustyo(v as Perustyo)}
        >
          {PERUSTYO_VAIHTOEHDOT.map((opt) => (
            <RadioButton
              key={opt.arvo}
              value={opt.arvo}
              hintText={t(`oikeudet:perustyo.${opt.arvo}.kuvaus`)}
            >
              {t(`oikeudet:perustyo.${opt.arvo}.nimi`)}
            </RadioButton>
          ))}
        </RadioButtonGroup>
      </Kortti>

      <Kortti>
        <SektioOtsikko>{t("otsikot.lisaoikeudet")}</SektioOtsikko>
        <CheckboxGroup labelText="Lisäoikeudet" labelMode="hidden">
          {!piilotaRaportit && (
            <Checkbox
              checked={lisaRaportit || adminValittu}
              disabled={adminValittu}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setLisaRaportit(e.target.checked)
              }
              hintText={t("vinkit.raportit")}
            >
              {t("labelText.raportit")}
            </Checkbox>
          )}
          <Checkbox
            checked={lisaMyonto || adminValittu}
            disabled={adminValittu}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setLisaMyonto(e.target.checked)
            }
            hintText={t("vinkit.oikeudet")}
          >
            {t("labelText.oikeudet")}
          </Checkbox>
        </CheckboxGroup>
        {adminValittu && <AdminHuomio>{t("huomio.admin")}</AdminHuomio>}
      </Kortti>

      {lisaysEpaonnistui && (
        <InlineAlert status="error" labelText={t("labelText.lisaaminen")}>
          {t("virhe.lisaaminen")}
        </InlineAlert>
      )}

      <Toiminnot>
        <Button onClick={tallenna}>{t("napit.lisaa")}</Button>
        <Button variant="secondary" onClick={() => navigate("/kayttooikeudet")}>
          {t("napit.peruuta")}
        </Button>
      </Toiminnot>
    </Page>
  );
}
